import * as RadioGroup from '@radix-ui/react-radio-group';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';

import { Icon } from '../../../icons';

const StyledRadioGroupRoot = styled(RadioGroup.Root)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledRadioItem = styled(RadioGroup.Item)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 14px 0;
  width: 100%;
  background: transparent;
  cursor: pointer;
  border-top: 1px solid ${({ theme }) => theme.colors.warmWhite3};

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.warmWhite3};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const RadioIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 16px;
  background: ${({ checked, theme }) => (checked ? theme.colors.black : 'transparent')};
  border: 2px solid ${({ theme }) => theme.colors.black};
  border-radius: 50%;
  transition: 200ms background-color, border-color;
`;

const RadioIndicator = ({ checked }) => {
  const theme = useTheme();
  return (
    <RadioIndicatorContainer checked={checked} theme={theme}>
      {checked && <Icon name="tick" fill="white" size={24} strokeWidth={3} />}
    </RadioIndicatorContainer>
  );
};

RadioIndicator.propTypes = {
  checked: PropTypes.bool.isRequired,
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
  min-height: 1px;
  min-width: 1px;
`;

const SizeTitle = styled.div`
  font-family: 'Barlow', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.4;
  color: black;
  width: 100%;
  text-align: left;
`;

const SizePrice = styled.div`
  font-family: 'Barlow', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  color: grey70;
  width: 100%;
  text-align: left;
`;

const ProductSizesGroup = ({ sizes, value, onChange }) => {
  return (
    <StyledRadioGroupRoot value={value} onValueChange={onChange}>
      {sizes.map((option, index) => {
        const sizeValue = option.id || option.productVariationId || `option-${index}`;
        const sizeDisplay = option.size || option.name || `${option.millilitre}ml` || 'Unknown';
        const priceDisplay = option.prices?.formattedPrice || (option.priceDiff ? `+$${option.priceDiff}` : '+$0.00');

        return (
          <StyledRadioItem key={sizeValue} value={sizeValue} id={sizeValue}>
            <RadioIndicator checked={value === sizeValue} />
            <ContentContainer>
              <SizeTitle>{sizeDisplay}</SizeTitle>
              <SizePrice>{priceDisplay}</SizePrice>
            </ContentContainer>
          </StyledRadioItem>
        );
      })}
    </StyledRadioGroupRoot>
  );
};

ProductSizesGroup.propTypes = {
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      // New format
      size: PropTypes.string,
      prices: PropTypes.shape({
        formattedPrice: PropTypes.string,
      }),
      // Old format
      id: PropTypes.string,
      name: PropTypes.string,
      millilitre: PropTypes.number,
      priceDiff: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      productVariationId: PropTypes.string,
      isAvailable: PropTypes.bool,
      formattedKilojoules: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ProductSizesGroup.defaultProps = {
  value: undefined,
};

const ComposedProductSizesGroup = observer(ProductSizesGroup);
export { ComposedProductSizesGroup as ProductSizesGroup };
